/* eslint-disable */
import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { MultiselectOptionItem, UiConstants } from '../util/core-utils';
import { MemStorage } from './util/mem-storage';
import { environment } from '../../environments/environment';
import { Order, OrderType } from './util/services';
import { InvoiceBook } from './invoice/invoice-book/invoice-book.service';
import { DisabledItem } from '../util/search-utils';
import { RuntimeConfiguration } from './runtime-configuration';
import { LoggedInUserStorage } from './util/storages';
/* eslint-enable */

@Injectable()
export class InvoiceBookSearchService {

  constructor() {
  }

  getSearchData(request: InvoiceBookSearch.SearchDataGetRequest): Observable<InvoiceBookSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<InvoiceBookSearch.SearchDataResult>) => {
      try {
        observer.next(storage.read(request));
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  setSearchData(request: InvoiceBookSearch.SearchDataSetRequest): Observable<InvoiceBookSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<InvoiceBookSearch.SearchDataResult>) => {
      try {
        observer.next(storage.save(request));
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  resetSearchData(request: InvoiceBookSearch.SearchDataGetRequest): Observable<InvoiceBookSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<InvoiceBookSearch.SearchDataResult>) => {
      try {
        observer.next(storage.reset(request));
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  private createStorage(): InvoiceBookSearch.Storage {
    return new InvoiceBookSearch.Storage();
  }

}

export namespace InvoiceBookSearch {

  export interface SearchDataGetRequest {
  }

  export interface SearchDataSetRequest {
    searchData: SearchData;
  }

  export interface SearchDataResult {
    searchData: SearchData;
  }

  export interface SearchData {
    itemsPerPage: number;
    pageNumber: number;
    order: Order<InvoiceBook.OrderField>;
    prefix: string;
    comment: string;
    externalId: string;
    invoiceSettings: MultiselectOptionItem<number>[];
    disabled: DisabledItem | null;
  }

  interface StoredSearchRawDataBase {
    version: number;
  }

  interface StoredSearchRawData extends StoredSearchRawDataBase {
    itemsPerPage: number;
    pageNumber: number;
    order: Order<InvoiceBook.OrderField>;
    prefix: string;
    comment: string;
    externalId: string;
    invoiceSettings: MultiselectOptionItem<number>[];
    disabled: DisabledItem | null;
  }

  export class Storage {

    /**
     * Increment this version if you change the SearchData interface.
     */
    private static readonly VERSION: number = 4;

    private static readonly DEFAULT_SEARCH_DATA: SearchData = {
      itemsPerPage: UiConstants.pageNumbers[0],
      pageNumber: 1,
      order: {
        field: InvoiceBook.OrderField.ID,
        type: OrderType.DESC
      },
      prefix: '',
      comment: '',
      externalId: '',
      invoiceSettings: [],
      disabled: DisabledItem.active()
    };

    constructor() {
    }

    public read(request: SearchDataGetRequest): SearchDataResult {
      let rawBase: StoredSearchRawDataBase;
      try {
        const key = this.generateKey();
        const json = MemStorage.getItem(key);
        if (!json) {
          return this.createDefaultResult();
        }
        rawBase = JSON.parse(json);
      }
      catch (error) {
        return this.createDefaultResult();
      }
      if (Storage.VERSION === rawBase.version) {
        const raw: StoredSearchRawData = <StoredSearchRawData> rawBase;
        return this.fromRaw(raw);
      }
      return this.createDefaultResult();
    }

    public reset(request: SearchDataGetRequest): SearchDataResult {
      return this.save({
        searchData: Storage.DEFAULT_SEARCH_DATA,
      });
    }

    public save(request: SearchDataSetRequest): SearchDataResult {
      const key = this.generateKey();
      const raw = this.toRaw(request.searchData);
      const json = JSON.stringify(raw);
      MemStorage.setItem(key, json);
      return this.read(request);
    }

    private generateKey(): string {
      return 'UserId(' + LoggedInUserStorage.getInstance().getUserId() + ')-Search-Invoice-Book';
    }

    private createDefaultResult(): SearchDataResult {
      return {
        searchData: Storage.DEFAULT_SEARCH_DATA,
      };
    }

    private fromRaw(data: StoredSearchRawData): SearchDataResult {
      return {
        searchData: {
          itemsPerPage: data.itemsPerPage,
          pageNumber: data.pageNumber,
          order: data.order,
          prefix: data.prefix,
          comment: data.comment,
          externalId: data.externalId,
          invoiceSettings: data.invoiceSettings,
          disabled: data.disabled
        }
      };
    }

    private toRaw(data: SearchData): StoredSearchRawData {
      return {
        version: Storage.VERSION,
        itemsPerPage: data.itemsPerPage,
        pageNumber: data.pageNumber,
        order: data.order,
        prefix: data.prefix,
        comment: data.comment,
        externalId: data.externalId,
        invoiceSettings: data.invoiceSettings,
        disabled: data.disabled
      };
    }

  }

}
